import { supertypeClass, property, remote } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { Response } from 'express';

import { groupBy } from 'lodash';
import { createReadStream, statSync, unlink } from 'fs';

import { Policy } from '../customer/Policy';
import { PolicyAdminClient, PolicyAdminPacket } from '../policyAdmin/PolicyAdminClient';
import { PolicyInfoDoc } from '../webservice/PolicyInfoDoc';
import { AuthenticatingController } from '../AuthenticatingController';
import { CommonUtilsBackend } from '../static/CommonUtilsBackend';
import { DownloadFileTypes } from '../interfaces/CommonTypes';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton } from '../logger/UILoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class PolicyDocsController extends Everything {
  @property({ getType: () => AuthenticatingController })
  controller: AuthenticatingController;

  @property({ type: Array, getType: () => PolicyInfoDoc })
  policyDocs: Array<PolicyInfoDoc> = [];

  @property()
  policyDocsInPA: boolean = false;

  // so we don't load policy docs unnecessarily
  loadingPolicyDocs: boolean = false;

  constructor() {
    super();
  }

  static new(controller: AuthenticatingController): PolicyDocsController {
    return new PolicyDocsController().init(controller);
  }

  init(controller) {
    this.controller = controller;
    return this;
  }

  async getPolicyDocs(policy: Policy) {
    const policyAdminPromise = this.getPolicyAdminDocs(policy);
    //Check to see if docs are in policy admin
    const docObject = await policyAdminPromise;

    if (!docObject.docs.length) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'PolicyDocsController',
          function: 'getPolicyDocs',
          category: UICategory.request,
        },
        'Policy Docs not found in Policy Admin'
      );
    }

    return policyAdminPromise;
  }

  @remote({ on: 'server' })
  async getPolicyAnnualStatement(policy: Policy) {
    this.loadingPolicyDocs = true;

    return PolicyAdminClient.getAnnualStatement(policy)
      .then((policyAdminPacket: PolicyAdminPacket) => {
        this.policyDocs = [];
        const policyDocLocations = [];
        let message;

        if (policyAdminPacket && policyAdminPacket.payload && policyAdminPacket.payload.length) {
          let latestDoc;
          let latestYear = 0;

          /**
           * This is necessary because we aren't guaranteed to get the documents back in the correct order.
           * Each doc has a name property in the style "Annual Statement [2020]". This doesn't seem like
           * the safest, as it will break if the naming convention changes. It will however default to whatever
           * is first in the array.
           */
          policyAdminPacket.payload.forEach((doc) => {
            if (!latestDoc) {
              latestDoc = doc;
              latestYear = parseInt(doc.name.match(/(\d+)/)[0]);
            } else {
              const docYear = parseInt(doc.name.match(/(\d+)/)[0]);
              if (docYear > latestYear) {
                latestDoc = doc;
                latestYear = docYear;
              }
            }
          });

          this.policyDocs.push(
            PolicyInfoDoc.new({
              documentDate: latestDoc.createdTime,
              documentId: latestDoc.data,
              documentType: 'POLICY',
              policyNumber: policy.policyNumber,
            })
          );
          policyDocLocations.push(0);
        }

        this.loadingPolicyDocs = false;

        if (this.policyDocs.length === 0) {
          LoggerSingleton.getInstance().warn(
            {
              module: 'PolicyDocsController',
              function: 'getPolicyAnnualStatement',

              category: Category.request,
            },
            'No documents were found for viewing'
          );

          message = 'Documents not yet available';
        } else {
          LoggerSingleton.getInstance().info(
            {
              module: 'PolicyDocsController',
              function: 'getPolicyAnnualStatement',

              category: Category.request,
            },
            'Documents were found in Policy Admin'
          );

          this.policyDocsInPA = true;
        }

        return {
          docs: this.policyDocs,
          message: message,
        };
      })
      .catch((err) => {
        this.loadingPolicyDocs = false;

        LoggerSingleton.getInstance().error(
          {
            module: 'PolicyDocsController',
            function: 'getPolicyAnnualStatement',

            category: Category.request,
            error: {
              name: err?.name,
              message: err?.message,
              stack: err?.stack,
              isHumanRelated: false,
            },
          },
          `There was a problem loading policy annual statement`
        );
        return {
          docs: [],
          message: 'There was a problem loading policy annual statement',
          error: err ? err.message || err : err,
        };
      });
  }

  @remote({ on: 'server' })
  private getPolicyAdminDocs(policy: Policy) {
    this.loadingPolicyDocs = true;

    return PolicyAdminClient.getPolicyPacket(policy)
      .then((policyAdminPacket: PolicyAdminPacket) => {
        this.policyDocs = [];
        const policyDocLocations = [];
        let message = null;

        if (policyAdminPacket && policyAdminPacket.payload) {
          policyAdminPacket.payload.forEach((doc) => {
            this.policyDocs.push(
              PolicyInfoDoc.new({
                documentDate: doc.createdTime,
                documentId: doc.data,
                documentType: 'POLICY',
                policyNumber: policy.policyNumber,
              })
            );
            policyDocLocations.push(0);
          });
        }

        this.loadingPolicyDocs = false;

        if (this.policyDocs.length === 0) {
          LoggerSingleton.getInstance().warn(
            {
              module: 'PolicyDocsController',
              function: 'getPolicyAdminDocs',

              category: Category.request,
            },
            'No documents were found for viewing'
          );

          message = 'Documents not yet available';
        } else {
          LoggerSingleton.getInstance().info(
            {
              module: 'PolicyDocsController',
              function: 'getPolicyAdminDocs',

              category: Category.request,
            },
            'Documents were found in Policy Admin'
          );

          this.policyDocsInPA = true;
        }

        return {
          docs: this.policyDocs,
          message: message,
        };
      })
      .catch((err) => {
        this.loadingPolicyDocs = false;

        LoggerSingleton.getInstance().error(
          {
            module: 'PolicyDocsController',
            function: 'getPolicyAdminDocs',

            category: Category.request,
            error: {
              name: err?.name,
              message: err?.message,
              stack: err?.stack,
              isHumanRelated: false,
            },
          },
          `There was a problem loading policy documents`
        );
        return {
          docs: [],
          message: 'There was a problem loading policy documents',
          error: err,
        };
      });
  }

  async openLatestPolicyDoc(response: Response, viewFile = true): Promise<void> {
    let indexOfLatestDoc = 0;

    for (let i = 0; i < this.policyDocs.length; i++) {
      if (this.policyDocs[i].docDate > this.policyDocs[indexOfLatestDoc].docDate) {
        indexOfLatestDoc = i;
      }
    }

    return this.openPolicyAdminDoc(response, indexOfLatestDoc, viewFile);
  }

  async openPolicyAdminDoc(response: Response, index, viewFile = true): Promise<void> {
    if (index >= this.policyDocs.length || index < 0) {
      throw new Error('Invalid Index when loading Policy Admin Doc: ' + index);
    }

    const selectedDoc = this.policyDocs[index];
    const streamableDoc = {
      type: selectedDoc.type as DownloadFileTypes, // POLICY_DOC_TYPES.pdf
      doc: selectedDoc.docID,
      fileNameWithExtension: selectedDoc.fileName, // PolicyDoc.fileNameWithExtension
    };
    await CommonUtilsBackend.downloadFile(response, streamableDoc, viewFile);
  }
}
